import { Center, Loader, Stack } from "@mantine/core";
import { useOktaAuth } from "@okta/okta-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Text, Title } from "../components-ui";
import {
  clearClaimReauthAttempts,
  signOutUser,
} from "../helpers/authHelper";

const SIGN_OUT_TIMEOUT_MS = 15_000;

const isStillAuthenticated = (oktaAuth: {
  authStateManager: { getAuthState: () => { isAuthenticated?: boolean } | null };
}) => oktaAuth.authStateManager.getAuthState()?.isAuthenticated ?? false;

export default function LogoutPage() {
  const { oktaAuth, authState } = useOktaAuth();
  const navigate = useNavigate();
  const signOutStartedRef = useRef(false);
  const signOutAttemptRef = useRef(0);
  const [signOutFailed, setSignOutFailed] = useState(false);

  const finishSignOut = useCallback(
    (attemptId: number) => {
      if (attemptId !== signOutAttemptRef.current) {
        return;
      }

      if (isStillAuthenticated(oktaAuth)) {
        signOutStartedRef.current = false;
        setSignOutFailed(true);
        return;
      }

      clearClaimReauthAttempts();
      setSignOutFailed(false);
      signOutStartedRef.current = false;
      navigate("/", { replace: true });
    },
    [navigate, oktaAuth]
  );

  const startSignOut = useCallback(() => {
    if (signOutStartedRef.current) {
      return;
    }

    signOutStartedRef.current = true;
    const attemptId = signOutAttemptRef.current + 1;
    signOutAttemptRef.current = attemptId;

    void signOutUser(oktaAuth)
      .then(() => finishSignOut(attemptId))
      .catch(() => finishSignOut(attemptId));
  }, [finishSignOut, oktaAuth]);

  useEffect(() => {
    if (!authState) {
      return;
    }

    if (!authState.isAuthenticated) {
      if (signOutFailed) {
        clearClaimReauthAttempts();
        setSignOutFailed(false);
        navigate("/", { replace: true });
        return;
      }

      if (!signOutStartedRef.current) {
        clearClaimReauthAttempts();
        navigate("/", { replace: true });
      }
      return;
    }

    if (signOutFailed || signOutStartedRef.current) {
      return;
    }

    startSignOut();
  }, [authState, navigate, signOutFailed, startSignOut]);

  useEffect(() => {
    if (!signOutStartedRef.current || signOutFailed || !authState?.isAuthenticated) {
      return;
    }

    const attemptId = signOutAttemptRef.current;
    const timeoutId = setTimeout(() => {
      if (attemptId !== signOutAttemptRef.current) {
        return;
      }

      signOutAttemptRef.current += 1;
      signOutStartedRef.current = false;
      setSignOutFailed(true);
    }, SIGN_OUT_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [authState, signOutFailed]);

  if (signOutFailed) {
    return (
      <Stack
        align="center"
        justify="center"
        spacing="md"
        style={{ minHeight: 600 }}
      >
        <Title order={2} testid="logout-failed-title">
          Unable to log out
        </Title>
        <Text size="md" testid="logout-failed-message">
          We could not complete sign-out. Please try again.
        </Text>
        <Button testid="logout-retry" onClick={() => setSignOutFailed(false)}>
          Try again
        </Button>
      </Stack>
    );
  }

  return (
    <Center style={{ minHeight: 600 }}>
      <Loader />
    </Center>
  );
}
